import { MusicXmlUtils } from "score_data";
import { EmptyDTO, HttpUtils, RDTO } from "ts_utils";
import { ContestEntryDTO, ContestEntryUpdateDTO, CreateUserDTO, TeamDTO,
	TeamUpdateDTO,
	TeamUploadDTO, UserDTO } from "../data/dtos";
import { FbAuth } from "./firebase/FbAuth";

export class Services {
	public static rootApiPath:string;
	public static recorderAceApiPath:string;
	public static usersApiVersion:string = "2";
	public static raApiVersion:string = "1";
	
	/**returns a user's information
	 * If the user is found, the [RDTO.data] property will point to the 
	 * [UserDTO] and [RDTO.success] will be true.
	 * 
	 * If there is a problem, [RDTO.success] will be false. Additionally, if
	 * the user is not found, the [RDTO.message] property will be set to 
	 * [RdtoMessage.NOT_FOUND].*/
	 public static async getUserInfo():Promise<RDTO<UserDTO>> {
		let token:string = await FbAuth.getUserToken();
		try {
			let url = U(`users/`);
			let req = await HttpUtils.get(url, { userToken: token });
			return new RDTO<UserDTO>(req.response, () => new UserDTO());
		}
		catch (e){
			console.log(e.toString());
			let rdto = new RDTO<UserDTO>();
			rdto.exception = e;
			return rdto;
		}
	}
	
	/**attempts to create user in database.
	 * 
	 * If the creation is successful, the [RDTO.data] property holds the 
	 * [UserDTO] object and the [RDTO.success] property is true.
	 * 
	 * If there is any problem, [RDTO.success] will be false.
	 * If a matching user been previously uploaded, [RTDO.message] will be set 
	 * to [RdtoMessage.ALREADY_EXISTS].*/
	public static async createUser(dto:CreateUserDTO):Promise<RDTO<UserDTO>> {
		let token:string = await FbAuth.getUserToken();
		try {
			let url = U(`users/`);
			let req = await HttpUtils.post(url, { userToken: token,
				formData: dto.toForm() });
			
			return new RDTO<UserDTO>(req.response, () => new UserDTO());
		}
		catch (e){
			console.log(e.toString());
			let rdto = new RDTO<UserDTO>();
			rdto.exception = e;
			return rdto;
		}
	}
	
	public static async removeUser(userId:number):Promise<RDTO<any>> {
		let token:string = await FbAuth.getUserToken();
		try {
			let url = U(`users/${userId}`);
			let req = await HttpUtils.delete(url, { userToken: token });
			
			return new RDTO(req.response, () => new EmptyDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO();
			rdto.exception = err;
			return rdto;
		}
	}
	
	public static async loadSongXml(path:string):Promise<string> {
		try {
			return await MusicXmlUtils.getMusicXmlStringFromUrl(path);
		}
		catch(err){
			throw err;
		}
	}
	
	public static async getTeamByCode(code:string):Promise<RDTO<TeamDTO>> {
		let url = L(`Teams/ByCode/${code}`);
		let token:string = await FbAuth.getUserToken();
		try {
			let req = await HttpUtils.get(url, { userToken: token });
			return new RDTO<TeamDTO>(req.response, () => new TeamDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<TeamDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	public static async getTeamsForUser(userId:number)
			:Promise<RDTO<TeamDTO>>{
		let url = L(`Teams/ByUser/${userId}`);
		let token:string = await FbAuth.getUserToken();
		try {
			let req = await HttpUtils.get(url, { userToken: token });
			return new RDTO<TeamDTO>(req.response, () => new TeamDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<TeamDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	/**Attempts to upload a team.
	 * If the upload is successful, the [RDTO.data] property holds the 
	 * [TeamDTO] object and the [RDTO.success] property is true.
	 * 
	 * If there is any problem, [RDTO.success] will be false. If the code
	 * is already taken, [RTDO.message] will be set to "code taken". If the name
	 * is already taken, [RDTO.message] will be set to "name taken".*/
	public static async addTeam(dto:TeamUploadDTO):Promise<RDTO<TeamDTO>> {
		let url = L("Teams");
		let token:string = await FbAuth.getUserToken();
		try {
			let req = await HttpUtils.post(url, { formData: dto.toForm(), 
				userToken: token });
			return new RDTO<TeamDTO>(req.response, () => new TeamDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<TeamDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	public static async updateTeam(dto:TeamUpdateDTO):Promise<RDTO<TeamDTO>> {
		let url = L(`Teams/${dto.id}`);
		let token:string = await FbAuth.getUserToken();
		try {
			let req = await HttpUtils.post(url, { formData: dto.toForm(), 
				userToken: token, method: "PATCH" });
			return new RDTO<TeamDTO>(req.response, () => new TeamDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<TeamDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	/**Attempts to remove a team.
	 * If the removal is successful, the [RDTO.success] property is true.
	 * 
	 * If there is any problem, [RDTO.success] will be false. If the team was
	 * not found, [RDTO.message] will contain [RdtoMessage.NOT_FOUND].*/
	public static async removeTeam(teamId:number):Promise<RDTO<EmptyDTO>> {
		let url:string = L(`Teams/${teamId}`);
		let token:string = await FbAuth.getUserToken();
		try {
			let req = await HttpUtils.delete(url, { userToken: token });
			return new RDTO<EmptyDTO>(req.response, () => new EmptyDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<EmptyDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	/**Gets the information for a list of teams. 
	 * The [RDTO.dataList] property will point to the [List<TeamDTO>] 
	 * object and [RDTO.success] will be true. If the list is returned 
	 * successfully but contains 0 teams, [RDTO.message] will contain 
	 * [RdtoMessage.NOT_FOUND].
	 * 
	 * If there is a problem, [RDTO.success] will be false. */
	// public static async getTeams():Promise<RDTO<TeamDTO>> {
	// 	let url = L("Teams");
	// 	try {
	// 		let req = await HttpUtils.get(url, { });
	// 		return new RDTO<TeamDTO>(req.response, () => new TeamDTO());
	// 	}
	// 	catch (err){
	// 		console.log(err.toString());
	// 		let rdto = new RDTO<TeamDTO>();
	// 		rdto.exception = err;
	// 		return rdto;
	// 	}
	// }
	
	/**Attempts to update the score for the current contest
	 * If the upload is successful, the [RDTO.success] property is true.
	 * If there is any problem, [RDTO.success] will be false. */
	public static async updateScore(dto:ContestEntryUpdateDTO)
			:Promise<RDTO<EmptyDTO>> {
		let url = L(`Teams/${dto.teamId}`);
		try {
			let req = await HttpUtils.post(url, { formData: dto.toForm() });
			return new RDTO<EmptyDTO>(req.response, () => new EmptyDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<EmptyDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
	
	/**Gets the information for a list of contest entries that match the 
	 * [dateId]. The [RDTO.dataList] property will point to the 
	 * [List<ContestEntryDTO>] object and [RDTO.success] will be true. If the 
	 * list is returned successfully but contains 0 teams, [RDTO.message] will 
	 * contain [RdtoMessage.NOT_FOUND].
	 * 
	 * If there is a problem, [RDTO.success] will be false. */
	public static async getContestData(dateId:string)
			:Promise<RDTO<ContestEntryDTO>> {
		let url = L(`Contests?date=${dateId}`);
		try {
			let req = await HttpUtils.get(url, {});
			return new RDTO<ContestEntryDTO>(req.response, 
				() => new ContestEntryDTO());
		}
		catch (err){
			console.log(err.toString());
			let rdto = new RDTO<ContestEntryDTO>();
			rdto.exception = err;
			return rdto;
		}
	}
}

const U = function(s:string, withTime:boolean = false):string {
	let out = (s.indexOf("?") !== -1)
		? `${Services.rootApiPath}${s}&apiv=${Services.usersApiVersion}`
		: `${Services.rootApiPath}${s}?apiv=${Services.usersApiVersion}`;
	if (withTime){
		out += `&_=${Date.now()}`;
	}
	
	return out;
}

const L = function(s:string, withTime:boolean = false):string {
	let out = (s.indexOf("?") !== -1)
		? `${Services.recorderAceApiPath}${s}&apiv=${Services.raApiVersion}`
		: `${Services.recorderAceApiPath}${s}?apiv=${Services.raApiVersion}`;
	if (withTime){
		out += `&_=${Date.now()}`;
	}
	
	return out;
}

const D = function(s:string):string {
	return (s.indexOf("?") !== -1)
		? s + `&_=${Date.now()}`
		: s + `?_=${Date.now()}`;
}